import React from "react";
import { useForm } from "react-hook-form";
import Input from "../../components/ui/Input";
import PrimaryButton from "../../components/ui/PrimaryButton";
import TextArea from "../../components/ui/TextArea";
import RadioButton from "../../components/ui/RadioButton";
import { timezoneData } from "./timezone_data";
import Select from "../../components/ui/Select";
import { createAgent } from "../../services/agentService";
import { useMutation } from "@tanstack/react-query";
import FileInput from "../../components/ui/FileInput";

const ConfigPage = () => {
	const { register, watch, handleSubmit } = useForm();

	//country data for country select
	const countryData = timezoneData.map((item) => {
		return {
			value: item.IsoAlpha2,
			text: item.CountryName,
		};
	});
	const watchCountry = watch("country", "");

	const countrySpecificTimezones = timezoneData
		.find((item) => item.IsoAlpha2 === watchCountry)
		?.WindowsTimeZones.map((item) => {
			return {
				value: item.Name,
				text: item.Name,
			};
		});

	// data for agent start and stop times
	const hourOptions = [
		"1",
		"2",
		"3",
		"4",
		"5",
		"6",
		"7",
		"8",
		"9",
		"10",
		"11",
		"12",
	].map((item) => {
		return { value: item, text: item };
	});
	const minuteOptions = ["00", "30"].map((item) => {
		return { value: item, text: item };
	});
	const ampmOptions = [
		{ value: "am", text: "AM" },
		{ value: "pm", text: "PM" },
	];

	// submitting the form to backend

	const mutation = useMutation(createAgent);

	const handleFormSubmit = (agentData) => {
		// making a multipart-formdata from the agentData received above, to be sent to backend.
		// The loop below just separately handles text values and files uploaded.
		let formData = new FormData();
		for (let key in agentData) {
			if (agentData.hasOwnProperty(key)) {
				if (key === "context_files") {
					let fileList = agentData[key];
					for (let i = 0; i < fileList.length; i++) {
						formData.append("context_files", fileList[i]);
					}
				} else {
					formData.append(key, agentData[key]);
				}
			}
		}
		mutation.mutate(formData);
	};

	return (
		<>
			<form onSubmit={handleSubmit((data) => handleFormSubmit(data))}>
				<div className="flex w-full min-h-full px-16">
					<div className="grow w-[50%] mt-2">
						<TextArea
							id="context"
							label="Your Context"
							register={register}
							placeholder="Write your text here..."
							rows={23}
						/>
						<FileInput
							id="context_files"
							label="Context Files"
							register={register}
							type="file"
							placeholder="Upload context files"
							required={false}
							multiple={true}
						/>
					</div>
					<div className="grow ml-8">
						<div>
							<Input
								id="agent_name"
								label="Agent Name"
								register={register}
								type="text"
								placeholder="Enter Agent Name"
								required={true}
							/>
							<Input
								id="agent_company_name"
								label="Company Name"
								register={register}
								type="text"
								placeholder="Enter Company Name"
								required={true}
							/>
							<Select
								id="country"
								label="Country"
								placeholder="Select Country..."
								register={register}
								required={true}
								options={countryData}
							/>
							{watchCountry && (
								<Select
									id="timezone"
									label="Timezone"
									placeholder="Select Timezone..."
									register={register}
									required={true}
									options={countrySpecificTimezones}
								/>
							)}

							<Input
								id="languages"
								label="Language(s)"
								register={register}
								type="text"
								placeholder="Select Language(s)"
								required={true}
							/>

							<Input
								id="no_of_parallel_calls"
								label="No of parallel calls"
								register={register}
								type="number"
								placeholder="Enter number of parallel calls required"
								required={true}
							/>

							<label className="block mt-2 text-sm font-medium text-gray-900">
								Summary
							</label>

							<div className="flex">
								<RadioButton
									id="per_day"
									label="Per day"
									register={register}
									value="per_day"
									name="summary_type"
									key="per_day"
									defaultChecked="checked"
								/>
								<RadioButton
									id="per_call"
									label="Per call"
									register={register}
									value="per_call"
									name="summary_type"
									key="per_call"
								/>
							</div>

							<div className="flex w-full justify-between">
								<div className="flex flex-col w-full">
									<label
										htmlFor="hours"
										className="block mb-2 text-sm font-medium text-gray-900 ">
										Start time
									</label>
									<div className="flex items-center justify-between w-full">
										<Select
											id="startHours"
											noLabel={true}
											noPlaceholder={true}
											register={register}
											className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 mr-2"
											options={hourOptions}
										/>
										<Select
											id="startMinutes"
											noLabel={true}
											noPlaceholder={true}
											register={register}
											className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 mr-2"
											options={minuteOptions}
										/>
										<Select
											id="startampm"
											noLabel={true}
											noPlaceholder={true}
											register={register}
											className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 mr-2"
											options={ampmOptions}
										/>
									</div>
								</div>
								<div className="flex flex-col w-full">
									<label
										htmlFor="hours"
										className="block mb-2 text-sm font-medium text-gray-900 ">
										Stop time
									</label>
									<div className="flex items-center justify-between">
										<Select
											id="stopHours"
											noLabel={true}
											noPlaceholder={true}
											register={register}
											className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 mr-2"
											options={hourOptions}
										/>
										<Select
											id="stopOptions"
											noLabel={true}
											noPlaceholder={true}
											register={register}
											className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 mr-2"
											options={minuteOptions}
										/>
										<Select
											id="stopampm"
											noLabel={true}
											noPlaceholder={true}
											register={register}
											className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 mr-2"
											options={ampmOptions}
										/>
									</div>
								</div>
							</div>

							<PrimaryButton type="submit" full="true" text="Submit" />
						</div>
					</div>
				</div>
			</form>
		</>
	);
};

export default ConfigPage;
